import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormGroup, FormControl, Validators, ValidatorFn, AbstractControl} from '@angular/forms';
import {ItemDataService} from '@emm/common-items/services/item-data.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {take, startWith, map as rxMap} from 'rxjs/operators';
import {CreateVariation, Variation} from '@emm/common-items/models/variation.model';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {LocationSetService} from '@emm/core/services/location-set.service';
import {LocationSetTypeService} from '@emm/core/services/location-set-type.service';
import {LocationSet} from '@emm/core/models/location-set.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {forkJoin, Observable} from 'rxjs';
import {formatFieldName} from '@emm/core/util/string-format.util';
import {EffectiveDateService} from '@emm/common-items/services/effective-date.service';
@Component({
    selector: 'emm-create-variation-dialog',
    templateUrl: './create-variation-dialog.component.html',
    styleUrl: './create-variation-dialog.component.scss',
    standalone: false
})
export class CreateVariationDialogComponent implements OnInit {

    effectiveDate: Date | null;
    templateForm: FormGroup;
    form: FormGroup;
    variationTemplates = [];
    formReady = false;
    templateAttributes;
    separatorKeysCodes: number[] = [ENTER, COMMA];

    allLocationSetTypes: LocationSetType[] = [];
    locationSetTypes: LocationSetType[] = [];
    locationSets: LocationSet[] = [];
    filteredLocationSets: Observable<LocationSet[]>;
    selectedLocationSetType: LocationSetType | null = null;
    selectedLocationSet: LocationSet | null = null;
    selectedTabIndex = 0;
    existingVariations: Variation[] = [];
    metadataAttributes: any[] = [];
    overrideAttributes: any[] = [];


    constructor(
        private itemDataService: ItemDataService,
        private notificationService: NotificationService,
        private locationSetService: LocationSetService,
        private locationSetTypeService: LocationSetTypeService,
        private dialogRef: MatDialogRef<CreateVariationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { item: any, existingVariations?: Variation[] },
        private effectiveDateService: EffectiveDateService,
    ) {
        this.existingVariations = data?.existingVariations || [];
        this.effectiveDate = this.effectiveDateService.effectiveDate;
        this.templateForm = new FormGroup({
            template: new FormControl(null, Validators.required),
            effectiveDate: new FormControl(this.effectiveDate),
            locationSetType: new FormControl('', Validators.required),
            location: new FormControl('', Validators.required),
        });
    }

    ngOnInit() {
        this.setupAutocomplete();
        this.loadLocationSetTypes();

        const itemClass = this.data.item.class || this.data.item.objectType;
        this.itemDataService.getVariationTemplates(itemClass).pipe().subscribe({
            next: (response) => {
                this.variationTemplates = response;
                this.buildForm();
                this.filterLocationSetTypesByTemplate();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    duplicateVariationLabelValidator(): ValidatorFn {
        return (control: AbstractControl): Record<string, any> | null => {
            if (!control.value) {
                return null;
            }
            const isDuplicate = this.existingVariations.some(
                v => v.variationLabel?.toLowerCase() === control.value?.toLowerCase()
            );
            return isDuplicate ? {duplicateVariationLabel: {value: control.value}} : null;
        };
    }

    private mapDetailToAttribute(detail: any): any {
        const attributeType = detail.attributeType;
        let uiType = attributeType === 'LIST' ? 'TEXT' : attributeType;
        const isDefaultTagNoOptions = this.isDefaultTagWithNoOptions(detail);

        if (isDefaultTagNoOptions) {
            uiType = 'TEXT';
        }

        return {
            jsonProperty: detail.jsonProperty,
            type: uiType,
            rawType: attributeType,
            name: formatFieldName(detail.name),
            description: detail.description,
            required: detail.required,
            options: this.processOptions(detail.options, attributeType, isDefaultTagNoOptions),
            defaultValue: detail.defaultValue
        };
    }

    private isDefaultTagWithNoOptions(detail: any): boolean {
        return (detail.tag === 'DEFAULT_TAG' || detail.jsonProperty === 'defaultTag') &&
            (!detail.options || (typeof detail.options === 'string' && detail.options.trim() === ''));
    }

    private processOptions(options: any, attributeType: string, isDefaultTagNoOptions: boolean): any {
        if (typeof options === 'string') {
            return this.parseStringOptions(options, attributeType);
        }

        if (Array.isArray(options)) {
            return options.map(o => typeof o === 'string' ? { value: o, label: o } : o);
        }

        return this.getDefaultOptions(attributeType, isDefaultTagNoOptions);
    }

    private parseStringOptions(optionsString: string, attributeType: string): any[] {
        const rawList = optionsString.split(',').map(o => o.trim()).filter(o => o.length);

        if (attributeType === 'BOOL') {
            return rawList.map(o => ({
                value: o.toLowerCase() === 'true',
                label: o.charAt(0).toUpperCase() + o.slice(1).toLowerCase()
            }));
        }

        // Turn underscores into spaces & capitalize the first letter of each word
        return rawList.map(o => ({
            value: o,
            label: o.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        }));
    }

    private getDefaultOptions(attributeType: string, isDefaultTagNoOptions: boolean): any {
        if (attributeType === 'BOOL') {
            return [
                { value: true, label: 'True' },
                { value: false, label: 'False' }
            ];
        }

        if (attributeType === 'SELECT' || attributeType === 'MULTI_SELECT') {
            return isDefaultTagNoOptions ? null : [];
        }

        return null;
    }

    private addMetadataAttributes(): void {
        const metadataFields = [
            {
                jsonProperty: 'variationDescription',
                name: 'Variation Description',
                description: 'Description that identifies what this variation will be used for'
            },
            {
                jsonProperty: 'variationLabel',
                name: 'Variation Label',
                description: 'Label that identifies where this variation will be used'
            }
        ];

        metadataFields.forEach(field => {
            this.templateAttributes.unshift({
                ...field,
                type: 'TEXT',
                rawType: 'TEXT',
                required: true,
                options: null,
                defaultValue: ''
            });
        });
    }

    private createFormControl(attribute: any): FormControl {
        const validators = this.getValidators(attribute);
        const initialValue = this.getInitialValue(attribute);
        const isDisabled = attribute.jsonProperty === 'disabled';

        return new FormControl({
            value: initialValue,
            disabled: isDisabled
        }, validators);
    }

    private getValidators(attribute: any): any[] {
        const validators = [];

        if (attribute.jsonProperty === 'variationLabel' || attribute.jsonProperty === 'variationDescription') {
            validators.push(Validators.required);
        }

        if (attribute.jsonProperty === 'variationLabel') {
            validators.push(this.duplicateVariationLabelValidator());
        }

        return validators;
    }

    private getInitialValue(attribute: any): any {
        if (attribute.jsonProperty === 'disabled') {
            return true;
        }

        let initialValue = attribute.defaultValue ?? null;

        switch (attribute.rawType) {
            case 'NUM':
                if (initialValue !== null && initialValue !== '') {
                    const num = Number(initialValue);
                    initialValue = isNaN(num) ? null : num;
                }
                break;
            case 'BOOL':
                if (typeof initialValue === 'string') {
                    initialValue = initialValue.toLowerCase() === 'true';
                } else if (typeof initialValue !== 'boolean') {
                    initialValue = false;
                }
                break;
            case 'LIST':
                // Represent list as comma-separated string for editing as TEXT
                if (Array.isArray(initialValue)) {
                    initialValue = initialValue.join(', ');
                } else if (initialValue == null) {
                    initialValue = '';
                }
                break;
            case 'MULTI_SELECT':
                if (!Array.isArray(initialValue)) initialValue = [];
                break;
        }

        return initialValue;
    }

    atLeastOneRequiredFieldValidator(): ValidatorFn {
        return (formGroup: AbstractControl): Record<string, any> | null => {
            if (!this.templateAttributes || this.templateAttributes.length === 0) {
                return null;
            }

            const hasAtLeastOneFilledRequiredField = this.templateAttributes
                .filter(attr => attr.required)
                .some(attr => {
                    const control = formGroup.get(attr.jsonProperty);
                    if (!control) return false;

                    const value = control.value;

                    if (value === null || value === undefined || value === '') {
                        return false;
                    }

                    if (Array.isArray(value)) {
                        return value.length > 0;
                    }

                    return true;
                });

            return hasAtLeastOneFilledRequiredField ? null : { atLeastOneRequired: true };
        };
    }

    setupAutocomplete() {
        this.filteredLocationSets = this.templateForm.controls['location'].valueChanges.pipe(
            startWith(''),
            rxMap(value => this._filterLocationSets(value || ''))
        );
    }

    buildForm() {
        this.formReady = false;
        const currentTemplate = this.variationTemplates.find(template =>
            template.id === this.templateForm.controls['template'].value);
        if (!currentTemplate) {
            this.templateAttributes = [];
            this.form = new FormGroup({});
            this.formReady = true;
            return;
        }

        this.templateAttributes = (currentTemplate.attributeDetails || []).map(detail =>
            this.mapDetailToAttribute(detail)
        );

        // Add metadata attributes at the beginning
        this.addMetadataAttributes();

        this.metadataAttributes = this.templateAttributes.filter(attr =>
            attr.jsonProperty === 'variationLabel' || attr.jsonProperty === 'variationDescription'
        );

        this.overrideAttributes = this.templateAttributes.filter(attr =>
            attr.jsonProperty !== 'variationLabel' && attr.jsonProperty !== 'variationDescription'
        ).sort((a, b) => {
            if (a.required === b.required) {
                return (a.name || '').localeCompare(b.name || '');
            }
            return a.required ? -1 : 1;
        });

        const formGroup: Record<string, FormControl> = {};
        for (const attribute of this.templateAttributes) {
            formGroup[attribute.jsonProperty] = this.createFormControl(attribute);
        }

        this.form = new FormGroup(formGroup, this.atLeastOneRequiredFieldValidator());
        this.formReady = true;
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (!this.selectedLocationSetType) {
            this.notificationService.error('Please select a location set type');
            return;
        }

        if (!this.selectedLocationSet) {
            this.notificationService.error('Please select a valid location set');
            return;
        }

        const overrides: Record<string, unknown> = {};
        for (const attribute of this.templateAttributes) {
            // Skip variationLabel and variationDescription - they should be at the top level, not in overrides
            if (attribute.jsonProperty === 'variationLabel' || attribute.jsonProperty === 'variationDescription') {
                continue;
            }

            const control = this.form.get(attribute.jsonProperty);
            if (!control) continue;

            overrides[attribute.jsonProperty] = this.transformValueForSave(control.value, attribute.rawType);
        }

        const newVariation: CreateVariation = {
            base: false,
            itemTag: this.data.item.tag,
            locationId: this.selectedLocationSet.tag,
            overrides,
            variationLabel: this.form.get('variationLabel')?.value?.trim(),
            variationDescription: this.form.get('variationDescription')?.value?.trim(),
        };
        this.dialogRef.close(newVariation);
    }

    private transformValueForSave(value: any, rawType: string): any {
        switch (rawType) {
            case 'BOOL':
                return value;
            case 'NUM':
                if (value === '' || value === null) {
                    return null;
                }
                const num = Number(value);
                return isNaN(num) ? null : num;
            case 'LIST':
                if (typeof value === 'string') {
                    return value.split(/[,\n]/).map(v => v.trim()).filter(v => v.length);
                }
                return Array.isArray(value) ? value : [];
            case 'MULTI_SELECT':
                return Array.isArray(value) ? value : [];
            default:
                return value;
        }
    }

    onDateChange(event: any) {
        this.effectiveDate = event?.value ?? null;
    }

    loadLocationSetTypes() {
        this.locationSetTypeService.getAllLocationSetTypes().pipe().subscribe({
            next: (types: LocationSetType[]) => {
                this.allLocationSetTypes = types.filter(type => type?.tag !== 'VENUE');
                this.filterLocationSetTypesByTemplate();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.allLocationSetTypes = [];
                this.locationSetTypes = [];
            }
        });
    }

    filterLocationSetTypesByTemplate() {
        const currentTemplate = this.variationTemplates.find(template =>
            template.id === this.templateForm.controls['template'].value);

        if (!currentTemplate || !currentTemplate.locationScopes || currentTemplate.locationScopes.length === 0) {
            this.locationSetTypes = this.allLocationSetTypes;
            return;
        }

        const templateScopes = currentTemplate.locationScopes.map((scope: string) => scope.toUpperCase());
        this.locationSetTypes = this.allLocationSetTypes.filter(type =>
            templateScopes.includes(type.tag?.toUpperCase())
        );
    }

    onLocationSetTypeChange(event: any) {
        const selectedType = event.value;
        this.selectedLocationSetType = selectedType;
        this.selectedLocationSet = null;
        this.templateForm.controls['location'].setValue('');

        if (selectedType?.tag?.toUpperCase() === 'CHAIN') {
            this.autoSelectChainLocation();
        } else {
            this.loadLocationSets();
        }
    }

    loadLocationSets() {
        if (!this.selectedLocationSetType) {
            this.locationSets = [];
            return;
        }

        this.locationSetService.getAllLocationSetsByTag(this.selectedLocationSetType.tag).pipe().subscribe({
            next: (locationSets: LocationSet[]) => {
                this.locationSets = locationSets;
                this.setupAutocomplete();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.locationSets = [];
            }
        });
    }

    autoSelectChainLocation() {
        this.locationSetService.getAllLocationSetsByTag('CHAIN').pipe().subscribe({
            next: (locationSets: LocationSet[]) => {
                this.locationSets = locationSets;
                if (locationSets && locationSets.length > 0) {
                    const chainLocation = locationSets.find(ls => ls.tag?.toUpperCase() === 'CHAIN') || locationSets[0];
                    this.selectedLocationSet = chainLocation;
                    this.templateForm.controls['location'].setValue(chainLocation as any);
                }
                this.setupAutocomplete();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.locationSets = [];
            }
        });
    }

    _filterLocationSets(value: string | LocationSet): LocationSet[] {
        if (!value) {
            return this.locationSets;
        }

        if (typeof value !== 'string') {
            return this.locationSets;
        }
        const filterValue = value.toLowerCase();
        return this.locationSets.filter(ls =>
            ls.name?.toLowerCase().includes(filterValue) ||
            ls.tag?.toLowerCase().includes(filterValue)
        );
    }

    displayLocationSet(locationSet: LocationSet): string {
        return locationSet?.name || '';
    }

    onLocationSelected(event: any) {
        this.selectedLocationSet = event.option.value;
    }

    onTemplateChange() {
        this.selectedLocationSet = null;
        this.selectedLocationSetType = null;
        this.templateForm.controls['locationSetType'].setValue('');
        this.templateForm.controls['location'].setValue('');
        this.selectedTabIndex = 0;
        this.buildForm();
        this.filterLocationSetTypesByTemplate();
        this.autoSelectLocationSetTypeIfOnlyOne();
    }

    autoSelectLocationSetTypeIfOnlyOne() {
        const currentTemplate = this.variationTemplates.find(template =>
            template.id === this.templateForm.controls['template'].value);

        if (!currentTemplate || !currentTemplate.locationScopes) {
            return;
        }

        // Check if template only has one location scope
        if (currentTemplate.locationScopes.length === 1) {
            const scopeTag = currentTemplate.locationScopes[0]?.toUpperCase();

            // Find and select the location set type matching this scope
            const matchingType = this.allLocationSetTypes.find(type =>
                type.tag?.toUpperCase() === scopeTag
            );

            if (matchingType) {
                this.selectedLocationSetType = matchingType;
                this.templateForm.controls['locationSetType'].setValue(matchingType as any);

                // If it's CHAIN, also auto-select the location
                if (scopeTag === 'CHAIN') {
                    this.autoSelectChainLocation();
                } else {
                    // For other types, just load the location sets
                    this.loadLocationSets();
                }
            }
        }
    }
}
